<template>
  <div class="side-wrap">
    <!-- 메뉴 열기/닫기 버튼 -->
    <button
      class="close_menu_btn"
      :class="{ on: !isOpen }"
      @click="toggleMenu"
    >
      {{ isOpen ? '메뉴접기' : '메뉴열기' }}
    </button>

    <!-- 사이드 메뉴 -->
    <div v-show="isOpen" id="side">
      <h3 class="tit">{{ sideTitle }}</h3>
      <div class="treeleft">
        <ul id="snb">
          <li
            v-for="(menu, index) in sideMenusData"
            :key="menu.menuId || index"
            :class="[
              'depth-' + getLevel(menu),          
              { open: menu.open, on: menu.active }
            ]"
          >
            <!-- 1차 메뉴 -->
            <a
              href="#"
              :class="{ parent: menu.children && menu.children.length > 0, on: menu.active }"
              @click.prevent="handleMenuClick(menu)"
            >
              {{ menu.menuNm }}
              <span
                v-if="menu.children && menu.children.length > 0"
                class="unfd"
              >
                {{ menu.open ? ' 접기' : ' 펼치기' }}
              </span>
            </a>

            <!-- 2차 이후 재귀 렌더링 -->
            <transition name="fade">
              <ul v-show="menu.open && menu.children && menu.children.length > 0">
                <SideNode
                  v-for="child in menu.children"
                  :key="child.menuId"
                  :node="child"
                  @click-node="handleMenuClick"
                />
              </ul>
            </transition>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useMenuContext } from '@/composables/useMenuContext'
import SideNode from './SideNode.vue'
import { useMenuStore } from '@/store/menu.store'
import { countMenuAccess } from '@/service/stats.service'

const isOpen = ref(true)
const router = useRouter()

// 상단에서 내려주는 컨텍스트(타이틀용)
const { currentMenuType } = useMenuContext()

const menuStore = useMenuStore()

const props = defineProps({
  menus: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: ''
  },
  // DefaultLayout 에서 내려주는 현재 메뉴 객체
  currentMenu: {
    type: Object,
    default: null
  }
})

const sideTitle = computed(() => {
  return props.title || currentMenuType || ''
})

// 실제로 트리 상태(open/active)를 관리하는 로컬 데이터
const sideMenusData = ref(structuredClone(props.menus || []))

// menus가 바뀌면 로컬 트리 갱신 + 현재 메뉴 기준으로 펼치기
watch(
  () => props.menus,
  (newMenus) => {
    sideMenusData.value = structuredClone(newMenus || [])
    
    // 새로운 menus에 현재 메뉴가 포함되는지 확인
    const currentMenuId = props.currentMenu?.menuId
    if (currentMenuId) {
      const menuExists = findMenuInTree(sideMenusData.value, currentMenuId)
      if (menuExists) {
        // 포함되면 expandToCurrentMenu 호출
        expandToCurrentMenu()
      } else {
        resetAllStates(sideMenusData.value)
      }
    }
  },
  { deep: true }
)

// currentMenu 객체 자체를 감시 (ref 아님)
watch(
  () => props.currentMenu,
  () => {
    expandToCurrentMenu()
  },
  { immediate: true }
)

function toggleMenu() {
  isOpen.value = !isOpen.value
}

function getLevel(menuItem) {
  let level = 1
  let parent = menuItem.parent
  while (parent) {
    level++
    parent = parent.parent
  }
  return level
}

function handleMenuClick(menuItem) {
  // path(매핑된 실제 경로) 우선, 없으면 원본 menuLink 사용
  const menuLink = menuItem.path || menuItem.menuLink
  if (menuItem.menuType === 'LINK' && menuLink) {
    window.open(menuLink, '_blank', 'noopener,noreferrer')
    return
  }

  menuStore.setCurrentMenu(menuItem.menuId)

  if (menuLink && menuLink !== 'dir' && menuLink !== 'N') {
    // 메뉴 접속수 카운트
    countMenuAccess({ menuUrl: menuLink, menuId: menuItem.menuId }).catch(() => {})

    // 레거시 경로 형식 "/board-mng/board/list/:bbsId" → 쿼리 기반으로 변환
    const legacyBbsMatch = menuLink.match(/^(.*\/board-mng\/board\/list)\/([^/?#]+)$/)
    const normalizedLink = legacyBbsMatch
      ? `${legacyBbsMatch[1]}/${legacyBbsMatch[2]}`
      : menuLink

    const [pathPart, queryPart = ''] = normalizedLink.split('?')
    const linkQuery = Object.fromEntries(new URLSearchParams(queryPart).entries())
    const nextQuery = {
      ...linkQuery,
      ...(linkQuery.bbsId ? { bbsId: linkQuery.bbsId } : {}),
      menuId: menuItem.menuId
    }

    // 메뉴 간 이동은 keepAlive 상태 문제를 피하기 위해 하드 리프레시
    const targetUrl = router.resolve({ path: pathPart, query: nextQuery }).href
    const currentUrl = window.location.pathname + window.location.search
    if (targetUrl === currentUrl) {
      window.location.reload()
    } else {
      window.location.href = targetUrl
    }
    return
  }

  // 폴더형 메뉴 토글
  const wasOpen = !!menuItem.open
  if (isRootMenu(menuItem)) closeAllRootMenus()
  else closeSiblingMenus(menuItem)
  menuItem.open = !wasOpen
}


/* ---------- 헬퍼들 ---------- */

function isRootMenu(menuItem) {
  return sideMenusData.value.includes(menuItem)
}

/**
 * 트리에서 특정 menuId를 가진 메뉴 찾기
 */
function findMenuInTree(nodes, targetId) {
  for (const node of nodes) {
    if (node.menuId === targetId) return node
    if (node.children && node.children.length > 0) {
      const found = findMenuInTree(node.children, targetId)
      if (found) return found
    }
  }
  return null
}

function closeAllRootMenus() {
  sideMenusData.value.forEach(menu => {
    menu.open = false
    closeAllSubMenus(menu.children)
  })
}

function closeAllSubMenus(menus) {
  if (!menus || menus.length === 0) return
  menus.forEach(menu => {
    menu.open = false
    if (menu.children && menu.children.length > 0) {
      closeAllSubMenus(menu.children)
    }
  })
}

function closeSiblingMenus(targetMenu) {
  function searchAndClose(menus) {
    menus.forEach(menu => {
      if (menu.children && menu.children.length > 0) {
        if (menu.children.includes(targetMenu)) {
          menu.children.forEach(sibling => {
            if (sibling !== targetMenu) {
              sibling.open = false
              closeAllSubMenus(sibling.children)
            }
          })
        } else {
          searchAndClose(menu.children)
        }
      }
    })
  }
  searchAndClose(sideMenusData.value)
}

function resetAllStates(menus) {
  menus.forEach(menu => {
    menu.open = false
    menu.active = false
    if (menu.children && menu.children.length > 0) {
      resetAllStates(menu.children)
    }
  })
}

/**
 * 현재 메뉴(props.currentMenu)까지의 경로를 따라
 * 조상 노드들은 open=true, 대상 노드는 active=true 로 설정
 */
function expandToCurrentMenu() {
  const cur = props.currentMenu
  if (!cur || !sideMenusData.value.length) {
    if (import.meta.env.DEV) {
      
    }
    return
  }

  const targetId = cur.menuId
  if (import.meta.env.DEV) {
    
  }

  // 먼저 전부 초기화
  resetAllStates(sideMenusData.value)

  // DFS로 타겟 노드 찾으면서 경로 마킹
  const dfs = (nodes) => {
    let hasTargetInSubtree = false

    nodes.forEach(node => {
      let foundHere = node.menuId === targetId
      let foundInChild = false

      if (node.children && node.children.length > 0) {
        foundInChild = dfs(node.children)
      }

      if (foundHere) {
        node.active = true
        if (import.meta.env.DEV) {
          
        }
      }

      // 자신 혹은 자식 중에 타겟이 있으면 열어준다
      if (foundHere || foundInChild) {
        node.open = true
        hasTargetInSubtree = true
      }
    })

    return hasTargetInSubtree
  }

  const result = dfs(sideMenusData.value)
}
</script>


<style scoped>
.side-wrap {
  position: relative;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  max-height: 0;
}

:deep(#side #snb>li>ul>li ul li.depth-4 > a) {
  font-size: 12px;
  padding-left: 6px;
}


:deep(#side #snb>li>ul>li ul li.depth-5 > a) {
  font-size: 12px;
}
</style>
